import { formatParisDateTime, type OperationOrderRef } from '@flux/shared';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useState } from 'react';
import { createDataTableColumnHelper, DataTable } from '@/components/data-table/data-table.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { fetchOperationOrders } from './api.js';

const PAGE_SIZE = 50;

const helper = createDataTableColumnHelper<OperationOrderRef>();

const columns = helper.columns([
  helper.accessor('name', {
    header: 'Numéro',
    cell: (info) => <span className="whitespace-nowrap font-medium">{info.getValue()}</span>,
  }),
  helper.accessor('store', { header: 'Origine' }),
  helper.accessor('sentAt', {
    header: 'Transmise le',
    cell: (info) => {
      const value = info.getValue();
      return value ? (
        <span className="whitespace-nowrap tabular-nums">
          {formatParisDateTime(new Date(value))}
        </span>
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
  }),
]);

export interface OperationHistoryTableProps {
  operationId: string;
}

/**
 * Commandes concernées par une opération (`02` §10, onglet « Commandes ») :
 * `GET /api/operations/:id/orders`, paginé côté serveur. La page vit en état
 * local plutôt que dans l'URL : la page d'opération y garde déjà son onglet
 * courant.
 */
export function OperationHistoryTable({ operationId }: OperationHistoryTableProps): JSX.Element {
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: queryKeys.operations.orders(operationId, { page, pageSize: PAGE_SIZE }),
    queryFn: () => fetchOperationOrders(operationId, { page, pageSize: PAGE_SIZE }),
  });

  return (
    <DataTable
      columns={columns}
      data={query.data?.items ?? []}
      getRowId={(row) => row.orderId}
      total={query.data?.total ?? 0}
      page={page}
      pageSize={PAGE_SIZE}
      onPageChange={setPage}
      isLoading={query.isPending}
      isError={query.isError}
      {...(query.error instanceof ApiError ? { errorMessage: query.error.message } : {})}
      errorDetail={query.error instanceof ApiError ? query.error.detail : undefined}
      onRetry={() => {
        void query.refetch();
      }}
      emptyState={{
        title: 'Aucune commande',
        description: 'Aucune commande n’a encore reçu cette opération.',
      }}
    />
  );
}
